import React, { useEffect, useState, useRef } from 'react';
import { getEvents } from '../api';
import { 
  Card, CardContent, Typography, Button, CircularProgress, Grid, Container,
  FormControl, InputLabel, Select, MenuItem, Box, Fab
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ClearIcon from '@mui/icons-material/Clear';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import dayjs from 'dayjs';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import InFeedAd from './ads/InFeedAd';
import AD_CONFIG from './ads/AdConfig';
import { getBlogForEvent } from '../config/blogMapping';
import { useNavigate } from 'react-router-dom';
import { 
  getEventKeyFromEvent,
  getShareIdForEvent,
  buildSnapshotFromEvent,
  isFavorite as isFavoriteKey,
  toggleFavorite as toggleFavoriteForSnapshot,
  getFavoritesCount,
  reconcileWithDataset,
  getIdStrategy
} from '../utils/favorites';
import { trackFavoritesAdd, trackFavoritesRemove, trackFavoritesToggleView } from '../utils/analytics';

function EventList() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState('');
  // Determine initial dance type filter based on URL, e.g. /app/tango -> "Tango"
  const pathTypeToFilter = () => {
    // Get last non-empty segment of pathname
    const segments = window.location.pathname.split('/').filter(Boolean);
    const lastSeg = segments[segments.length - 1]?.toLowerCase() || '';
    const map = {
      tango: 'Tango',
      fusion: 'Fusion',
      salsa: 'Salsa',
      bachata: 'Bachata',
      zouk: 'Brazilian Zouk',
      brazilianzouk: 'Brazilian Zouk',
      meditation: 'Meditation',
      yoga: 'Yoga',
      dance: 'Dance',
      ecstaticdance: 'Ecstatic Dance',
      'ecstatic-dance': 'Ecstatic Dance',
      improvisation: 'Improvisation',
      swing: 'West Coast Swing',
      wcs: 'West Coast Swing',
      all: 'All'
    };
    return map[lastSeg] || 'All';
  };

  const [danceTypeFilter, setDanceTypeFilter] = useState(pathTypeToFilter());

  // Helper to extract city from Location string
  const extractCity = (locationStr = '') => {
    const parts = locationStr.split(',');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      // Identify the state segment containing "CA" possibly with ZIP
      if (part === 'CA' || part.endsWith('CA') || part.startsWith('CA') || /\bCA\b/.test(part)) {
        if (i > 0) return parts[i - 1].trim();
      }
    }
    // Fallback: if no "CA" found, use the first part as city (handles "Richmond", etc.)
    return parts[0].trim() || null;
  };

  // Get unique locations for filter dropdown
  const locations = [...new Set(events.map(ev => extractCity(ev.Location)).filter(Boolean))]
    .filter(loc => loc && !loc.includes(':') && !loc.includes('Level'))
    .sort();

  const navigate = useNavigate();
  // State for Back to Top button visibility
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents();
        // Filter out "No event here" entries and sort the remaining events
        const filteredData = data.data.filter(event => 
          event['Event Name'] && !event['Event Name'].includes('No event')
        );
        const sortedEvents = filteredData.sort((a, b) => a.Date.localeCompare(b.Date));
        setEvents(sortedEvents);
        setFilteredEvents(sortedEvents);
        setLoading(false);
        // Reconcile favorites with today's dataset
        const keys = new Set(sortedEvents.map(ev => getEventKeyFromEvent(ev)));
        const todayDatasetDate = dayjs().format('YYYY-MM-DD');
        reconcileWithDataset({ datasetDate: todayDatasetDate, currentEventKeys: keys });
        setFavoritesCount(getFavoritesCount());
      } catch (err) {
        setError('Failed to fetch events');
        setLoading(false);
      }
    };
  
    fetchEvents();
  }, []);

  // Read URL parameters and apply filters on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const danceType = urlParams.get('danceType');
    const location = urlParams.get('location');
    const date = urlParams.get('date');
    
    if (danceType) {
      setDanceTypeFilter(danceType);
    }
    if (location) {
      setLocationFilter(location);
    }
    if (date) {
      setDateFilter(dayjs(date));
    }
  }, []);

  // Show Back to Top button when scrolled beyond threshold
  useEffect(() => {
    const handleScroll = () => {
      const threshold = 400; // pixels scrolled before showing button
      setShowBackToTop(window.pageYOffset > threshold);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply filters whenever filter values change
  useEffect(() => {
    let filtered = [...events];

    // Favorites filter (pure filter bubble)
    if (favoritesOnly) {
      filtered = filtered.filter(ev => isFavoriteKey(getEventKeyFromEvent(ev)));
    }

    if (dateFilter) {
      filtered = filtered.filter(event => {
        const eventDate = dayjs(event.Date);
        return eventDate.format('YYYY-MM-DD') === dateFilter.format('YYYY-MM-DD');
      });
    }

    // Location filter
    if (locationFilter) {
      filtered = filtered.filter(event => {
        const city = extractCity(event.Location);
        return city && city.toLowerCase() === locationFilter.toLowerCase();
      });
    }

    // Dance type filter - look at both Event Name and Description
    if (danceTypeFilter && danceTypeFilter !== 'All') {
      filtered = filtered.filter(event => {
        const eventName = event['Event Name'].toLowerCase();
        const description = event.Description.toLowerCase();
        const textToSearch = `${eventName} ${description}`;
        const source = event.source || '';

        // Source-based classification first - these are EXCLUSIVE
        // If an event has a specific source, it should ONLY match its corresponding filter
        if (source === 'tangomango') {
          return danceTypeFilter === 'Tango';
        }
        if (source === 'bayareafusion') {
          return danceTypeFilter === 'Fusion';
        }
        if (source === 'salsavida') {
          return danceTypeFilter === 'Salsa';
        }
        if (source.startsWith('googlecalendar')) {
          return danceTypeFilter === 'Improvisation';
        }
        if (source.startsWith('nextgenswing')) {
          return danceTypeFilter === 'West Coast Swing';
        }
        if (source === 'omni_bachata') {
          return danceTypeFilter === 'Bachata';
        }
        if (source === 'omni_zouk') {
          return danceTypeFilter === 'Brazilian Zouk';
        }
        
        // Fallback to keyword analysis for events without specific sources
        switch(danceTypeFilter) {
          case 'Meditation':
            return (eventName.includes('meditation') || 
                   textToSearch.includes('meditation class') ||
                   (textToSearch.includes('meditation') && !textToSearch.includes('meditative movement'))) &&
                   !textToSearch.includes('yoga') &&
                   !textToSearch.includes('vinyasa');
          case 'Yoga':
            return (eventName.includes('yoga') || 
                   textToSearch.includes('yoga class') ||
                   textToSearch.includes('vinyasa')) &&
                   !eventName.startsWith('meditation');
          case 'Fusion':
            return textToSearch.includes('fusion') &&
                   !textToSearch.includes('milonga') && 
                   !textToSearch.includes('tango') && 
                   !textToSearch.includes('practica') &&
                   !textToSearch.includes('milonguera');
          case 'Tango':
            return textToSearch.includes('milonga') || 
                   textToSearch.includes('tango') || 
                   textToSearch.includes('practica') ||
                   textToSearch.includes('milonguera');
          case 'Ecstatic Dance':
            return textToSearch.includes('ecstatic dance');
          case 'West Coast Swing':
            return textToSearch.includes('west coast swing') || textToSearch.includes('wcs');
          case 'Bachata':
            return textToSearch.includes('bachata');
          case 'Salsa':
            return textToSearch.includes('salsa');
          case 'Brazilian Zouk':
            return textToSearch.includes('zouk');
          case 'Dance':
            return (textToSearch.includes('dance') && 
              !textToSearch.includes('tango') && 
              !textToSearch.includes('milonga') && 
              !textToSearch.includes('practica') && 
              !textToSearch.includes('milonguera') && 
              !textToSearch.includes('ecstatic dance') &&
              !textToSearch.includes('fusion') &&
              !eventName.includes('yoga') &&  
              !textToSearch.includes('yoga class') &&
              !eventName.includes('meditation') &&
              !textToSearch.includes('meditation class'));
          default:
            return false;
        }
      });
    }

    setFilteredEvents(filtered);
  }, [dateFilter, locationFilter, danceTypeFilter, favoritesOnly, events]);

  // Smoothly scroll to top when a filter changes (but skip initial mount)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dateFilter, locationFilter, danceTypeFilter, favoritesOnly]);

  // Dynamic event type extraction
  const getEventTypes = () => {
    const types = new Set();
    
    events.forEach(event => {
      const eventName = event['Event Name'].toLowerCase();
      const description = event.Description.toLowerCase();
      const textToSearch = `${eventName} ${description}`;
      const source = event.source || '';
      
      // Source-based classification first
      if (source === 'tangomango') {
        types.add('Tango');
        return;
      }
      if (source === 'bayareafusion') {
        types.add('Fusion');
        return;
      }
      if (source === 'salsavida') {
        types.add('Salsa');
        return;
      }
      if (source.startsWith('googlecalendar')) {
        types.add('Improvisation');
        return;
      }
      if (source.startsWith('nextgenswing')) {
        types.add('West Coast Swing');
        return;
      }
      if (source === 'omni_bachata') {
        types.add('Bachata');
        return;
      }
      if (source === 'omni_zouk') {
        types.add('Brazilian Zouk');
        return;
      }
      
      // Fallback to keyword analysis for other sources
      // Check for Meditation first
      if ((eventName.includes('meditation') || 
           textToSearch.includes('meditation class') ||
           (textToSearch.includes('meditation') && !textToSearch.includes('meditative movement'))) &&
           !textToSearch.includes('yoga') &&
           !textToSearch.includes('vinyasa')) {
        types.add('Meditation');
        return;
      }
      
      // Check for Yoga
      if ((eventName.includes('yoga') || 
           textToSearch.includes('yoga class') ||
           textToSearch.includes('vinyasa')) &&
           !eventName.startsWith('meditation')) {
        types.add('Yoga');
        return;
      }

      // Check for Fusion
      if (textToSearch.includes('fusion') &&
          !textToSearch.includes('milonga') && 
          !textToSearch.includes('tango') && 
          !textToSearch.includes('practica') &&
          !textToSearch.includes('milonguera')) {
        types.add('Fusion');
        return;
      }
      
      // Check for Tango events
      if (textToSearch.includes('milonga') || 
          textToSearch.includes('tango') || 
          textToSearch.includes('practica') ||
          textToSearch.includes('milonguera')) {
        types.add('Tango');
        return;
      }
      
      // Check for Ecstatic Dance
      if (textToSearch.includes('ecstatic dance')) {
        types.add('Ecstatic Dance');
        return;
      }

      // Check for other Dance events
      if (textToSearch.includes('dance') && 
          !textToSearch.includes('tango') && 
          !textToSearch.includes('milonga') && 
          !textToSearch.includes('practica') && 
          !textToSearch.includes('milonguera') && 
          !textToSearch.includes('ecstatic dance') &&
          !textToSearch.includes('fusion') &&
          !eventName.includes('yoga') &&
          !textToSearch.includes('yoga class') &&
          !eventName.includes('meditation') &&
          !textToSearch.includes('meditation class')) {
        types.add('Dance');
        return;
      }
    });

    return ['All', ...Array.from(types).sort()];
  };

  // Replace static danceTypes with dynamic types
  const eventTypes = getEventTypes();

  // Helper to classify a single event for display badge
  const getEventDisplayType = (event) => {
    const eventName = (event['Event Name'] || '').toLowerCase();
    const description = (event.Description || '').toLowerCase();
    const textToSearch = `${eventName} ${description}`;
    const source = event.source || '';

    // Source-based classification first (mirrors filter logic)
    if (source === 'tangomango') return 'Tango';
    if (source === 'bayareafusion') return 'Fusion';
    if (source === 'salsavida') return 'Salsa';
    if (source.startsWith('googlecalendar')) return 'Improvisation';
    if (source.startsWith('nextgenswing')) return 'West Coast Swing';
    if (source === 'omni_bachata') return 'Bachata';
    if (source === 'omni_zouk') return 'Brazilian Zouk';

    // Fallback to keyword analysis
    if ((eventName.includes('meditation') || 
        textToSearch.includes('meditation class') ||
        (textToSearch.includes('meditation') && !textToSearch.includes('meditative movement'))) &&
        !textToSearch.includes('yoga') &&
        !textToSearch.includes('vinyasa')) {
      return 'Meditation';
    }
    if ((eventName.includes('yoga') || 
        textToSearch.includes('yoga class') ||
        textToSearch.includes('vinyasa')) &&
        !eventName.startsWith('meditation')) {
      return 'Yoga';
    }
    if (textToSearch.includes('fusion') &&
        !textToSearch.includes('milonga') && 
        !textToSearch.includes('tango') && 
        !textToSearch.includes('practica') &&
        !textToSearch.includes('milonguera')) {
      return 'Fusion';
    }
    if (textToSearch.includes('milonga') || 
        textToSearch.includes('tango') || 
        textToSearch.includes('practica') ||
        textToSearch.includes('milonguera')) {
      return 'Tango';
    }
    if (textToSearch.includes('ecstatic dance')) {
      return 'Ecstatic Dance';
    }
    if (textToSearch.includes('west coast swing') || textToSearch.includes('wcs')) {
      return 'West Coast Swing';
    }
    if (textToSearch.includes('dance') && 
        !textToSearch.includes('tango') && 
        !textToSearch.includes('milonga') && 
        !textToSearch.includes('practica') && 
        !textToSearch.includes('milonguera') && 
        !textToSearch.includes('ecstatic dance') &&
        !textToSearch.includes('fusion') &&
        !eventName.includes('yoga') &&  
        !textToSearch.includes('yoga class') &&
        !eventName.includes('meditation') &&
        !textToSearch.includes('meditation class')) {
      return 'Dance';
    }
    return null;
  };

  // Build a safe CSS class for the badge from the display type
  const getEventTypeClass = (event) => {
    const t = getEventDisplayType(event);
    return t ? `event-type--${t.replace(/\s+/g, '-')}` : '';
  };

  // Filter controls component (layout-only adjustments for PR3)
  const FilterControls = () => (
    <Box className="filter-grid" sx={{ mb: 1 }}>
      <Button
        variant={favoritesOnly ? 'contained' : 'outlined'}
        color={favoritesOnly ? 'secondary' : 'primary'}
        onClick={() => {
          const next = !favoritesOnly;
          setFavoritesOnly(next);
          if (next) {
            // When enabling Favorites-only, clear other filters to avoid over-restricting the view
            setDateFilter(null);
            setLocationFilter('');
            setDanceTypeFilter('All');
          }
          trackFavoritesToggleView({ enabled: next, favorites_count: getFavoritesCount() });
        }}
        aria-pressed={favoritesOnly}
        aria-label="My Favorites"
      >
        My Favorites{favoritesCount > 0 ? ` (${favoritesCount})` : ''}
      </Button>

      <DatePicker
        label="Filter by Date"
        value={dateFilter}
        onChange={(newValue) => setDateFilter(newValue)}
        format="YYYY-MM-DD"
        desktopModeMediaQuery="(min-width: 768px)"
        clearable
        slotProps={{
          textField: { sx: { minWidth: 200 } },
          actionBar: {
            actions: ['clear']
          }
        }}
      />

      <FormControl sx={{ minWidth: { xs: 0, sm: 200 } }}>
        <InputLabel>Location</InputLabel>
        <Select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          label="Location"
        >
          <MenuItem value="">All Locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: { xs: 0, sm: 200 } }}>
        <InputLabel>Event Type</InputLabel>
        <Select
          value={danceTypeFilter}
          onChange={(e) => setDanceTypeFilter(e.target.value)}
          label="Event Type"
        >
          {eventTypes.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* New Clear Filters button */}
      <Button
        variant="outlined"
        startIcon={<ClearIcon />}
        onClick={() => {
          setDateFilter(null);
          setLocationFilter('');
          setDanceTypeFilter('All');
          setFavoritesOnly(false);
        }}
        sx={{ alignSelf: 'stretch' }}
      >
        Clear Filters
      </Button>
    </Box>
  );

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      {/* Removed tall page title for PR2 slim header */}
      
      {/* PR3: Sticky, responsive filter wrapper */}
      <div className="filter-bar" role="region" aria-label="Filters">
        <FilterControls />
        <div style={{ marginTop: 8 }}>
          <a
            href="https://www.buymeacoffee.com/myeventsbridge"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            <img
              src="https://www.buymeacoffee.com/assets/img/custom_images/yellow_img.png"
              alt="Buy Me A Coffee"
              style={{ height: 50, width: 'auto' }}
            />
          </a>
        </div>
      </div>

      <div className="after-filter-content">
        {filteredEvents.length === 0 ? (
          <Typography variant="body1">
            No events found matching your filters. Try adjusting or clearing the filters.
          </Typography>
        ) : (
          <Grid container spacing={2} direction="column">
            {filteredEvents.map((event, index) => {
              const shouldShowAd = AD_CONFIG.enabled && 
                                 AD_CONFIG.placements.inFeed.enabled && 
                                 index > 0 && 
                                 (index + 1) % AD_CONFIG.placements.inFeed.frequency === 0;
              
              const blogInfo = getBlogForEvent(event['Event Name']);
              const hasBlog = blogInfo !== null;
              
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                <Card sx={{ 
                  border: hasBlog ? '1px solid #f59e0b' : 'default',
                  boxShadow: hasBlog ? 'none' : 'default'
                }}>
                  <CardContent>
                    {/* Event type badge (non-intrusive; layout-only) */}
                    {getEventDisplayType(event) && (
                      <div
                        className={`event-type-badge ${getEventTypeClass(event)}`}
                        aria-label={`Event type: ${getEventDisplayType(event)}`}
                      >
                        {getEventDisplayType(event)}
                      </div>
                    )}
                    <Typography variant="h5" component="div">
                      {event['Event Name']}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {event.Date} {event.Time}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {event.Location === 'TBD' ? 'Location: TBD' : event.Location}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {event.Description.split('\n')[0]}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', gap: 1, flex: '1 1 auto' }}>
                        <Button
                          onClick={() => {
                            const shareId = getShareIdForEvent(event);
                            const params = new URLSearchParams();
                            if (danceTypeFilter && danceTypeFilter !== 'All') {
                              params.append('danceType', danceTypeFilter);
                            }
                            if (locationFilter) {
                              params.append('location', locationFilter);
                            }
                            if (dateFilter) {
                              params.append('date', dateFilter.format('YYYY-MM-DD'));
                            }
                            const queryString = params.toString();
                            const url = queryString
                              ? `/event/${encodeURIComponent(shareId)}?${queryString}`
                              : `/event/${encodeURIComponent(shareId)}`;
                            navigate(url);
                          }}
                          variant="contained"
                          color="primary"
                        >
                          Details
                        </Button>
                        {event.Details && (
                          <Button
                            href={event.Details}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            color="primary"
                          >
                            Go to Site
                          </Button>
                        )}
                        {(() => {
                          const snapshot = buildSnapshotFromEvent(event);
                          const favored = isFavoriteKey(snapshot.key);
                          return (
                            <Button
                              onClick={() => {
                                const wasFav = isFavoriteKey(snapshot.key);
                                toggleFavoriteForSnapshot(snapshot);
                                const nowFav = !wasFav;
                                setFavoritesCount(getFavoritesCount());
                                const paramsBase = {
                                  event_key: snapshot.key,
                                  source: snapshot.source,
                                  id_strategy: getIdStrategy(),
                                  start_date: snapshot.date || null,
                                  start_time: snapshot.timeText || null,
                                  dataset_date: dayjs().format('YYYY-MM-DD'),
                                  ui_location: 'card',
                                  time_parsed: Boolean(snapshot.startDateTimeISO),
                                  location_present: Boolean(snapshot.location)
                                };
                                if (nowFav) {
                                  trackFavoritesAdd(paramsBase);
                                } else {
                                  trackFavoritesRemove(paramsBase);
                                }
                                if (favoritesOnly) {
                                  setFilteredEvents(prev => prev.filter(ev => {
                                    return isFavoriteKey(getEventKeyFromEvent(ev));
                                  }));
                                }
                              }}
                              aria-label={favored ? 'Remove from favorites' : 'Add to favorites'}
                              aria-pressed={favored}
                              variant={favored ? 'contained' : 'outlined'}
                              color="secondary"
                              startIcon={favored ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            >
                              Favorite
                            </Button>
                          );
                        })()}
                      </Box>
                      {hasBlog && (
                        <Button
                          onClick={() => {
                            // Build URL with current filter parameters
                            const params = new URLSearchParams();
                            if (danceTypeFilter && danceTypeFilter !== 'All') {
                              params.append('danceType', danceTypeFilter);
                            }
                            if (locationFilter) {
                              params.append('location', locationFilter);
                            }
                            if (dateFilter) {
                              params.append('date', dateFilter.format('YYYY-MM-DD'));
                            }
                            const queryString = params.toString();
                            const url = queryString ? `${blogInfo.blogUrl}?${queryString}` : blogInfo.blogUrl;
                            window.location.href = url;
                          }}
                          variant="contained"
                          startIcon={<AutoStoriesIcon />}
                          sx={{
                            backgroundColor: '#f59e0b',
                            color: '#ffffff',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#d97706',
                            },
                            minWidth: 'fit-content'
                          }}
                          aria-label={blogInfo.blogTitle}
                        >
                          My Experience
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
                  </Grid>
                  
                  {/* Show ad after every nth event */}
                  {shouldShowAd && (
                    <Grid item xs={12}>
                      <InFeedAd />
                    </Grid>
                  )}
                </React.Fragment>
              );
            })}
          </Grid>
        )}
      </div>

      {showBackToTop && (
        <Fab 
          color="primary" 
          size="small" 
          aria-label="Back to top" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
        >
          <ArrowUpwardIcon />
        </Fab>
      )}
    </Container>
  );
}

export default EventList;